import { AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

import Text from 'components/Text/Text';
import { WorkPageDataCMS } from 'containers/WorkPage/WorkPage.types';
import { useCopyStore, useGlobalStore } from 'store';
import { ReactComponent as SvgClose } from 'svgs/close.svg';
import { handleMouseEnter, handleMouseLeave } from 'utils/cursorGlobal';
import {
  filtersMotionProps,
  openFilterMotionProps,
} from 'utils/styles/animations';
import { ColorNames } from 'utils/styles/theme';

import * as S from './Filter.styles';

export interface FilterProps {
  filterText: string;
  tagsList: WorkPageDataCMS['filterPopup'];
}

const defaultProps: Partial<FilterProps> = {
  filterText: 'Show me',
  tagsList: {
    filterTagsList: [],
  },
};

const Filter: FunctionComponent<FilterProps> = ({
  filterText,
  tagsList,
  ...rest
}) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const { activeFilter, setActiveFilter } = useGlobalStore();

  const [filterName, setFilterName] = useState<string>(
    activeFilter || tagsList.filterTagsList[0].text
  );

  const filterRef = useRef<HTMLDivElement>(null);

  const hoverTextColor =
    globalSettings?.buttonsHoverColor ||
    globalSettings?.buttonsHoverDefaultColors;

  useEffect(() => {
    if (router.query.filter) {
      setActiveFilter(router.query.filter as string);

      const findFilter = tagsList.filterTagsList.find(
        filter => filter?.tag?.tag === router.query.filter
      );

      findFilter && setFilterName(router.query.filter as string);
    } else setActiveFilter('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  const openFilterModal = (
    <S.OpenModalWrapper {...openFilterMotionProps(filterRef)}>
      <S.FilterHeading>
        <Text as="small" size="small" text={filterText} />

        <SvgClose
          onClick={() => setIsOpen(false)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      </S.FilterHeading>

      <S.FiltersWrapper
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {tagsList.filterTagsList.map(({ text, tag, all }, index) => (
          <S.FilterItem
            key={`${text}-` + index}
            onClick={() => (
              setActiveFilter(tag?.tag || ''),
              setFilterName(text),
              setIsOpen(false)
            )}
            isActive={tag?.tag === activeFilter || (activeFilter === '' && all)}
            hoverTextColor={hoverTextColor}
            {...filtersMotionProps(index)}
          >
            <Text as="p" size="filter" text={text} />
          </S.FilterItem>
        ))}
      </S.FiltersWrapper>
    </S.OpenModalWrapper>
  );

  const closeFilterModal = (
    <S.MainContent
      ref={filterRef}
      initial={{ opacity: 1 }}
      animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
    >
      <Text as="small" size="small" text={filterText} />

      <S.ActiveFilterWrapper
        onClick={() => setIsOpen(true)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Text
          as="small"
          size="small"
          text={filterName}
          color={ColorNames.black}
        />
      </S.ActiveFilterWrapper>
    </S.MainContent>
  );

  return (
    <S.Wrapper isOpen={isOpen} role="modal" {...rest}>
      <AnimatePresence initial mode="wait">
        {isOpen ? openFilterModal : closeFilterModal}
      </AnimatePresence>
    </S.Wrapper>
  );
};

Filter.defaultProps = defaultProps;

export default memo(Filter);
